import React from 'react';
import InputCheckbox from '../../../atoms/InputCheckbox/InputCheckbox';

/**
 * LateDeliveryCheckbox — the "allow late delivery" control that every consumer of `DatePickerActions`
 * plugs into its `extraActions` slot (TAN-10453).
 *
 * Late delivery only means something when there IS a due date: without one there is no deadline to
 * extend. So the checkbox is disabled and forced OFF while the popover draft has no date, and the
 * toggle always flips the DISPLAYED value, never the raw draft one — otherwise a stale persisted
 * `true` would come back ON by itself as soon as a date appeared.
 *
 * The label is injected by the consumer (already translated): this component adds no user-facing text.
 */

type LateDeliveryCheckboxProps = {
  /** Date currently being edited inside the popover (`null` while there is none), as given by the slot. */
  draftDate: Date | null;
  /** Raw draft value of the slot. Only honoured while there is a draft date. */
  isChecked?: boolean;
  /** Already translated label, e.g. `t('assigments:Allow late delivery')`. */
  label: string;
  /** Receives the next value; wire it to the slot's `setValue`. */
  onChange: (isChecked: boolean) => void;
};

/** Shape `DatePickerActions` hands to `extraActions.render` (`value` is `undefined` after a Clear). */
type LateDeliveryRenderApi = {
  draftDate: Date | null;
  setValue: (next: boolean) => void;
  value: boolean | undefined;
};

function LateDeliveryCheckbox({ draftDate, isChecked = false, label, onChange }: LateDeliveryCheckboxProps): JSX.Element {
  const hasDraftDate = Boolean(draftDate);
  const isDeliverLateChecked = hasDraftDate && Boolean(isChecked);

  function handleChange(): void {
    onChange(!isDeliverLateChecked);
  }

  return <InputCheckbox checked={isDeliverLateChecked} disabled={!hasDraftDate} label={label} onChange={handleChange} />;
}

/**
 * Builds the `extraActions.render` callback for `DatePickerActions`, so the three consumers wire the
 * checkbox exactly the same way:
 *
 * `extraActions={{ initial: canDeliverLate, render: createLateDeliveryCheckboxRenderer(t('assigments:Allow late delivery')) }}`
 */
export function createLateDeliveryCheckboxRenderer(label: string): (api: LateDeliveryRenderApi) => JSX.Element {
  return function renderLateDeliveryCheckbox({ draftDate, setValue, value }: LateDeliveryRenderApi): JSX.Element {
    return <LateDeliveryCheckbox draftDate={draftDate} isChecked={value} label={label} onChange={setValue} />;
  };
}

export default LateDeliveryCheckbox;
